import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { COL } from '@eprintsmart/shared';
import { db } from '../firebase';
import {
  CRM_STAGES,
  STAGE_LABEL,
  addInteraction,
  sendWhatsApp,
  updateContact,
  watchInteractions,
  type CrmContact,
  type CrmInteraction,
  type CrmStage,
} from '../services/crm';

export function CrmContact() {
  const { id = '' } = useParams();
  const [contact, setContact] = useState<CrmContact | null>(null);
  const [timeline, setTimeline] = useState<CrmInteraction[]>([]);
  const [note, setNote] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    const unC = onSnapshot(doc(db, COL.crmContacts, id), (s) =>
      setContact(s.exists() ? { ...(s.data() as CrmContact), id: s.id } : null),
    );
    const unI = watchInteractions(id, setTimeline);
    return () => { unC(); unI(); };
  }, [id]);

  if (!contact) {
    return (
      <Box>
        <Button component={RouterLink} to="/crm" startIcon={<ArrowBackIcon />}>Back</Button>
        <Typography sx={{ mt: 2 }} color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  const saveNote = async () => {
    if (!note.trim()) return;
    await addInteraction(id, { type: 'note', body: note.trim(), direction: 'internal' });
    setNote('');
  };

  const send = async () => {
    if (!message.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await sendWhatsApp({ contactId: id, message: message.trim() });
      setMessage('');
    } catch (e) {
      const m = (e as { message?: string })?.message;
      setError(m && m.length < 200 ? m : 'Could not send. Is MSG91 configured?');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Button component={RouterLink} to="/crm" startIcon={<ArrowBackIcon />}>Back</Button>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 2 }} alignItems="flex-start">
        {/* Profile */}
        <Paper variant="outlined" sx={{ p: 2, width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
          <Typography variant="h6">{contact.name}</Typography>
          {contact.company && <Typography color="text.secondary">{contact.company}</Typography>}
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              select size="small" label="Stage" value={contact.stage}
              onChange={(e) => void updateContact(id, { stage: e.target.value as CrmStage })}
            >
              {CRM_STAGES.map((s) => <MenuItem key={s} value={s}>{STAGE_LABEL[s]}</MenuItem>)}
            </TextField>
            <Field label="Phone" value={contact.phone ?? ''} onSave={(v) => updateContact(id, { phone: v })} />
            <Field label="Email" value={contact.email ?? ''} onSave={(v) => updateContact(id, { email: v })} />
            <Field label="Source" value={contact.source ?? ''} onSave={(v) => updateContact(id, { source: v })} />
          </Stack>
        </Paper>

        {/* Timeline + actions */}
        <Box sx={{ flexGrow: 1, width: '100%' }}>
          {contact.phone ? (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Send WhatsApp</Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small" fullWidth multiline maxRows={4} placeholder={`Message to ${contact.phone}`}
                  value={message} onChange={(e) => setMessage(e.target.value)}
                />
                <Button variant="contained" onClick={() => void send()} disabled={busy || !message.trim()}>Send</Button>
              </Stack>
              {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
            </Paper>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>Add a phone number to message this contact on WhatsApp.</Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Add a note</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" fullWidth placeholder="Log a call, a preference, a next step…" value={note}
                onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void saveNote(); }} />
              <Button onClick={() => void saveNote()} disabled={!note.trim()}>Save</Button>
            </Stack>
          </Paper>

          <Typography variant="subtitle2" gutterBottom>Timeline</Typography>
          {timeline.length === 0 ? (
            <Typography color="text.secondary">Nothing yet.</Typography>
          ) : (
            <Stack spacing={1}>
              {timeline.map((t) => (
                <Paper key={t.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" label={t.type} />
                    <Chip size="small" variant="outlined" label={t.direction} />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {new Date(t.at).toLocaleString('en-IN')}
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 0.5 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{t.body}</Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

/** An inline-editable single-line field that saves on blur. */
function Field({ label, value, onSave }: { label: string; value: string; onSave: (v: string) => Promise<void> }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <TextField
      size="small" label={label} value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v !== value) void onSave(v.trim()); }}
    />
  );
}
